<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import OrdersApi from 'dashboard/api/orders';

const props = defineProps({
  contactId: {
    type: [Number, String],
    required: true,
  },
});

const { t } = useI18n();

const orders = ref([]);
const loading = ref(true);
const error = ref('');

const fetchOrders = async () => {
  if (!props.contactId) return;

  try {
    loading.value = true;
    error.value = '';
    const response = await OrdersApi.getByContact(props.contactId);
    orders.value = response.data.payload || response.data || [];
  } catch (e) {
    error.value =
      e.response?.data?.error || t('ORDERS_PAGE.ERRORS.LOAD_FAILED');
  } finally {
    loading.value = false;
  }
};

const formatCurrency = amount => {
  if (amount === null || amount === undefined) return '-';
  const num = Number(amount);
  return !Number.isNaN(num)
    ? new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }).format(num / 100)
    : amount;
};

const formatDate = dateString => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const getStatusColor = status => {
  const s = String(status || '').toLowerCase();
  if (s === 'completed' || s === 'captured')
    return 'text-n-teal-11 bg-n-teal-3';
  if (s === 'canceled' || s === 'failed') return 'text-n-ruby-11 bg-n-ruby-3';
  if (['processing', 'partially-shipped', 'shipped'].includes(s)) {
    return 'text-n-blue-11 bg-n-blue-3';
  }
  return 'text-n-amber-11 bg-n-amber-3';
};

const statusLabel = status => {
  const labels = {
    pending: () => t('ORDERS_PAGE.STATUS.PENDING'),
    processing: () => t('ORDERS_PAGE.STATUS.PROCESSING'),
    'partially-shipped': () => t('ORDERS_PAGE.STATUS.PARTIALLY_SHIPPED'),
    shipped: () => t('ORDERS_PAGE.STATUS.SHIPPED'),
    completed: () => t('ORDERS_PAGE.STATUS.COMPLETED'),
    canceled: () => t('ORDERS_PAGE.STATUS.CANCELED'),
    captured: () => t('ORDERS_PAGE.STATUS.CAPTURED'),
    failed: () => t('ORDERS_PAGE.STATUS.FAILED'),
  };
  return (labels[status] || labels.pending)();
};

const paymentMethodLabel = order => {
  const method = order.payment_methods?.[0];
  if (!method) return '';

  const labels = {
    pix_dynamic_code: () => t('ORDERS_PAGE.PAYMENT_METHODS.PIX_DYNAMIC_CODE'),
    boleto: () => t('ORDERS_PAGE.PAYMENT_METHODS.BOLETO'),
    payment_link: () => t('ORDERS_PAGE.PAYMENT_METHODS.PAYMENT_LINK'),
  };
  return labels[method] ? labels[method]() : method;
};

watch(
  () => props.contactId,
  () => {
    fetchOrders();
  },
  { immediate: true }
);
</script>

<template>
  <div class="px-4 py-2 text-n-slate-12">
    <div v-if="loading" class="flex justify-center items-center p-4">
      <Spinner size="32" class="text-n-brand" />
    </div>
    <div v-else-if="error" class="text-center text-n-ruby-11">
      {{ error }}
    </div>
    <div v-else-if="!orders.length" class="text-center text-n-slate-11 text-xs">
      {{ t('ORDERS_PAGE.EMPTY_TITLE') }}
    </div>
    <div v-else class="flex flex-col gap-3">
      <div
        v-for="order in orders"
        :key="order.ref_id"
        class="border border-n-weak rounded-md p-3 text-sm flex flex-col gap-2"
      >
        <div class="flex justify-between items-center">
          <span class="font-medium text-n-slate-12">
            {{ order.ref_id || `#${order.id}` }}
          </span>
          <span
            class="px-2 py-0.5 rounded text-xs font-semibold"
            :class="getStatusColor(order.order_status || order.status)"
          >
            {{ statusLabel(order.order_status || order.status) }}
          </span>
        </div>

        <div class="flex justify-between text-n-slate-11">
          <span>{{ formatCurrency(order.total_amount) }}</span>
          <span>{{ formatDate(order.created_at) }}</span>
        </div>

        <div class="flex justify-between text-n-slate-11">
          <span>{{ paymentMethodLabel(order) }}</span>
          <span :class="getStatusColor(order.payment_status)" class="text-xs">
            {{ statusLabel(order.payment_status) }}
          </span>
        </div>

        <div class="pt-2 border-t border-n-weak flex justify-end">
          <router-link
            v-if="order.conversation_id"
            :to="`/app/accounts/${$route.params.accountId}/conversations/${order.conversation_id}`"
            class="text-n-brand text-xs font-medium hover:underline"
          >
            {{ t('ORDERS_PAGE.VIEW_IN_CONVERSATION') }}
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>
